"use client";

import { useContext } from "react";
import { useRouter } from "next/navigation";

import { Button } from "@/components/Button";
import { showAuthModal } from "@/components/Header/Header";

import { useToast } from "@/hooks/useToast";
import { useLogin } from "@/hooks/auth/useLogin";
import { useQR, useQRStyle } from "@/hooks/useQR";

import { getTokenData } from "@/services/CookiesService";

import { omittedValues, getQRCodeUrl } from "@/utils/qrEditorHelper";
import { handleAuthModalForExpiredUsers } from "@/utils/authModalData";

import { QREditorContextProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { LayoutContext } from "@/contexts/LayoutContext";
import { ModalContext } from "@/contexts/ModalContext";

import { PreviewToggleProps } from "./types";

import "./styles.scss";

export const PreviewToggle = ({
  previewType,
  setPreviewType,
  isQRActivated,
  setQrRaw,
}: PreviewToggleProps) => {
  const { toast } = useToast();
  const { onHandleLogout } = useLogin();
  const { getRawData, updateQRCode } = useQRStyle();
  const { requestQR } = useQR();
  const router = useRouter();

  const modalContext = useContext(ModalContext);
  const layoutContext = useContext(LayoutContext);
  const { isQrLoading, setIsQrLoading, qrData, setQrData } =
    useContext<QREditorContextProps<any>>(QREditorContext);

  const getQRPreview = () => {
    const tokenData = getTokenData();
    if (tokenData?.isTokenExpired) {
      showAuthModal({
        formType: "login",
        modalContext,
        layoutContext,
        modalData: layoutContext?.modalData,
        onLoginSuccess: () =>
          handleAuthModalForExpiredUsers({
            formType: "login",
            modalContext,
            router,
            callback: () => getQRPreview(),
            logout: onHandleLogout,
          }),
        onRegisterSuccess: () =>
          handleAuthModalForExpiredUsers({
            formType: "register",
            modalContext,
            router,
            callback: () => getQRPreview(),
            logout: onHandleLogout,
          }),
        onRecoverPasswordSuccess: () =>
          handleAuthModalForExpiredUsers({
            formType: "recoverPassword",
            modalContext,
            router,
            callback: () => getQRPreview(),
            logout: onHandleLogout,
          }),
        notClosable: true,
        router,
      });
    } else {
      setIsQrLoading(false);
      setPreviewType("qr");
      updateQRCode({
        qrStyle: qrData.qrStyle,
        qrUrl: getQRCodeUrl(qrData, true),
      });
      if (requestQR) {
        setIsQrLoading(true);
        requestQR()
          .then(async (data: any) => {
            if (data !== undefined) {
              setQrData(
                {
                  ...qrData,
                  qrCodeId: data?.id || qrData.qrCodeId,
                  qrCodeReference: data?.reference || qrData.qrCodeReference,
                },
                [...omittedValues(qrData.data)]
              );
              const raw = await getRawData();
              setQrRaw && setQrRaw(raw);
              setIsQrLoading(false);
            }
          })
          .catch(() => {
            toast({ message: "genericError", type: "error", marginBottom: 80 });
          });
      }
    }
  };

  const modalLabel = isQrLoading ? "loading" : "QR Code";

  return (
    <div className="qr-preview__header__buttons-toggle">
      <Button
        type="button"
        dataQA="qr-editor-preview-button"
        secondary
        onClick={() => setPreviewType("data")}
        className={previewType === "qr" ? "deactivated" : undefined}
        label="Preview Page"
        customClass="button-toggle"
        square
      />
      <Button
        type="button"
        dataQA="qr-editor-qr-preview-button"
        secondary
        onClick={() => getQRPreview()}
        disabled={!isQRActivated}
        className={previewType === "data" ? "deactivated" : undefined}
        label={modalLabel}
        customClass="button-toggle"
        square
      />
    </div>
  );
};
